import { apiClient } from '@/lib/api/client'

// ─── Types ──────────────────────────────────────────────
//
// Mirrors lib/feeds/admin.js (tables: supabase/migrations/202610210003_product_feeds.sql).
// `attrs` and build `stats` keep their server keys: they are feed column names and exclusion
// reasons, not fields.

export type ItemKind = 'individual' | 'set'
export type ThemeLifecycle = 'incubating' | 'scaling' | 'graduated' | 'retired'
export type MarginBand = 'high' | 'mid' | 'low'
export type SetStatus = 'draft' | 'live' | 'retired'
export type Fetcher = 'google' | 'meta' | 'other' | 'unknown'

/** Exclusion reasons, in the order the build checks them (lib/feeds/render.js REASONS). */
export const REASONS = [
  'not_on_sale', 'ip_risk', 'ip_unchecked', 'mature', 'no_offer', 'no_image', 'no_price', 'no_link', 'ip_medium', 'piece_ineligible',
] as const
export type ExclusionReason = (typeof REASONS)[number]

export const REASON_LABEL: Record<string, string> = {
  not_on_sale: 'Not on sale (not ACTIVE / not on Online Store / not a live set)',
  ip_risk: 'IP risk (copyright high or rejected)',
  ip_unchecked: 'Advertised but not yet copyright-checked (check requested)',
  mature: 'Mature content',
  no_offer: 'No default offer for the mode',
  no_image: 'No image (no own-id leaning mockup)',
  no_price: 'No presentment price / unavailable',
  no_link: 'No product handle',
  ip_medium: 'Copyright medium (unclear attribution) — still allowed inside a set',
  piece_ineligible: 'A set piece is not eligible',
}

export const LIFECYCLES: ThemeLifecycle[] = ['incubating', 'scaling', 'graduated', 'retired']
export const MARGIN_BANDS: MarginBand[] = ['high', 'mid', 'low']

export interface BuildKindStats {
  eligible: number
  excluded: number
  reasons: Record<string, number>
}

export interface FeedBuildStats {
  timings?: { sync_ms?: number; prices_ms?: number; items_ms?: number }
  individual?: BuildKindStats
  set?: BuildKindStats
  removed?: number
  [key: string]: unknown
}

export interface FeedBuild {
  id: number
  status: 'running' | 'done' | 'failed'
  stats: FeedBuildStats
  error: string | null
  startedAt: string
  finishedAt: string | null
  durationMs: number | null
}

export interface FeedFetch {
  id: number
  file: string
  status: number
  rows: number | null
  userAgent: string | null
  fetcher: Fetcher
  at: string
}

export interface FeedDestination {
  slug: string
  channel: 'google' | 'meta'
  country: string
  currency: string
  language: string
  format: 'tsv' | 'csv'
  linkBase: string
  hasToken: boolean
  variantPolicy: { modes?: string[] }
  defaults: Record<string, string>
  enabled: boolean
  createdAt: string
  updatedAt: string
  builds: FeedBuild[]
  counts: Array<{ kind: ItemKind; eligible: boolean; n: number }>
  exclusions: Array<{ reason: string; kind: ItemKind; n: number }>
  fetches: FeedFetch[]
  lastGoogleFetch: FeedFetch | null
}

export interface FeedItemAttrs {
  title: string
  price: string
  link: string
  image_link: string
  brand: string
  availability: string
  size: string
  color: string
  custom_label_0: string
  custom_label_1: string
  custom_label_2: string
  custom_label_3: string
  custom_label_4: string
}

export interface FeedItem {
  itemId: string
  kind: ItemKind
  eligible: boolean
  exclusionReason: string | null
  artworkId: number | null
  setSlug: string | null
  attrs: FeedItemAttrs
  changedAt: string
}

export interface ItemParams {
  destination?: string
  kind?: ItemKind
  eligible?: boolean
  reason?: string
  theme?: string
  q?: string
  limit?: number
  cursor?: string
}

export interface MerchTheme {
  slug: string
  kind: 'room' | 'concept'
  name: string
  subtitle: string | null
  canonicalPath: string
  shopifyCollectionId: number | null
  seedConcepts: string[]
  kgUcids: string[]
  lifecycle: ThemeLifecycle
  marginBand: MarginBand | null
  updatedAt: string
  members?: number
  sets?: { total: number; live: number; draft: number; retired: number }
}

export interface ThemePatch {
  lifecycle?: ThemeLifecycle
  marginBand?: MarginBand | null
  seedConcepts?: string[]
}

export interface MerchSet {
  slug: string
  name: string
  themeSlug: string | null
  themeName: string | null
  themeLifecycle: ThemeLifecycle | null
  status: SetStatus
  statusReason: string | null
  handle: string | null
  imageUrl: string | null
  shopifyProductId: number | null
  pieces: number
  pieceHandles: string[]
  inFeed: boolean
  eligible: boolean | null
  exclusionReason: string | null
  price: string | null
  updatedAt: string
}

export interface RotatedToken {
  destination: string
  urls: Record<string, string>
}

export type ProposalType = 'sets' | 'queries'
export type ProposalStatus = 'proposed' | 'approved' | 'applied' | 'rejected' | 'failed' | 'superseded'
export const PROPOSAL_STATUSES: ProposalStatus[] = ['proposed', 'approved', 'applied', 'rejected', 'failed', 'superseded']

/** One row of either queue (lib/feeds/proposals.js). `payload`/`target`/`evidence` keep server keys. */
export interface Proposal {
  id: number
  type: ProposalType
  kind: string
  status: ProposalStatus
  /** set slug, or the search term / negative keyword */
  subject: string
  setName?: string | null
  imageUrl?: string | null
  themeSlug?: string | null
  payload?: Record<string, any>
  /** queries only: the Ads account the proposal belongs to; evidence money is in its currency */
  account?: { customerId: string; name: string | null; currency: string | null } | null
  target?: Record<string, any>
  evidence: Record<string, any>
  source: string
  error: string | null
  createdAt: string
  decidedAt: string | null
  appliedAt: string | null
}

export interface ProposalCount {
  type: ProposalType
  kind: string
  status: ProposalStatus
  n: number
}

export interface ProposalParams {
  type: ProposalType
  status?: ProposalStatus
  kind?: string
}

// ─── Client ─────────────────────────────────────────────

const BASE = '/api/feeds-admin'

export const feedsApi = {
  getDestinations: async (): Promise<{ data: FeedDestination[] }> =>
    (await apiClient.get(`${BASE}/destinations`)).data,

  build: async (slug: string, skipSync: boolean): Promise<{ destination: string; skipSync: boolean; started: boolean }> =>
    (await apiClient.post(`${BASE}/destinations/${slug}/build`, { skipSync })).data,

  rotateToken: async (slug: string): Promise<RotatedToken> =>
    (await apiClient.post(`${BASE}/destinations/${slug}/rotate-token`)).data,

  getItems: async (params: ItemParams): Promise<{ data: FeedItem[]; nextCursor: string | null }> =>
    (await apiClient.get(`${BASE}/items`, { params })).data,

  getSets: async (destination = 'google-us'): Promise<{ linkBase: string | null; data: MerchSet[] }> =>
    (await apiClient.get(`${BASE}/sets`, { params: { destination } })).data,

  getThemes: async (): Promise<{ data: MerchTheme[] }> => (await apiClient.get(`${BASE}/themes`)).data,

  updateTheme: async (slug: string, patch: ThemePatch): Promise<MerchTheme> =>
    (await apiClient.patch(`${BASE}/themes/${slug}`, patch)).data,

  getProposals: async (params: ProposalParams): Promise<{ counts: ProposalCount[]; data: Proposal[] }> =>
    (await apiClient.get(`${BASE}/proposals`, { params })).data,

  decideProposals: async (type: ProposalType, ids: number[], decision: 'approve' | 'reject'): Promise<{ decided: number; applying: number }> =>
    (await apiClient.post(`${BASE}/proposals/${type}/decision`, { ids, decision })).data,

  updateSet: async (slug: string, status: 'draft' | 'retired'): Promise<Pick<MerchSet, 'slug' | 'status' | 'statusReason' | 'handle'>> =>
    (await apiClient.patch(`${BASE}/sets/${slug}`, { status })).data,
}
